import { useCallback, useEffect, useRef, useState } from 'react';

export type BulkPrivacyChange = (imageIds: number[], isPublic: boolean) => Promise<number[]>;

export interface BulkPrivacyFailure {
  failedCount: number;
  totalCount: number;
}

export interface UseGalleryManageModeReturn {
  isManaging: boolean;
  selectedIds: Set<number>;
  isSaving: boolean;
  failure: BulkPrivacyFailure | undefined;
  enterManageMode: () => void;
  cancel: () => void;
  toggleSelected: (imageId: number) => void;
  setSelectedPrivacy: (isPublic: boolean) => void;
}

/**
 * Selection and bulk-save state for the grid's manage mode.
 *
 * `onBulkPrivacyChange` resolves with the ids that were actually stored, so a
 * partial failure keeps the mode open with only the failed ids still selected.
 */
const useGalleryManageMode = (
  onBulkPrivacyChange: BulkPrivacyChange | undefined
): UseGalleryManageModeReturn => {
  const [isManaging, setIsManaging] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [failure, setFailure] = useState<BulkPrivacyFailure | undefined>(undefined);

  // Guards against setting state after the gallery closes mid-save.
  const isMountedRef = useRef(true);
  // A save works from a snapshot, so changes wait for it to land.
  const isSavingRef = useRef(false);
  // A save that lands after its generation has nothing to write back to.
  const saveGenerationRef = useRef(0);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const enterManageMode = useCallback((): void => {
    saveGenerationRef.current += 1;
    isSavingRef.current = false;
    setIsManaging(true);
    setSelectedIds(new Set());
    setFailure(undefined);
  }, []);

  const cancel = useCallback((): void => {
    saveGenerationRef.current += 1;
    isSavingRef.current = false;
    setIsSaving(false);
    setIsManaging(false);
    setSelectedIds(new Set());
    setFailure(undefined);
  }, []);

  const toggleSelected = useCallback((imageId: number): void => {
    if (isSavingRef.current) return;

    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }, []);

  const setSelectedPrivacy = useCallback(
    (isPublic: boolean): void => {
      if (!onBulkPrivacyChange || isSavingRef.current) return;

      const imageIds = [...selectedIds];
      if (imageIds.length === 0) return;

      const generation = saveGenerationRef.current;
      isSavingRef.current = true;
      setIsSaving(true);
      setFailure(undefined);

      const save = async (): Promise<void> => {
        const succeededIds = await onBulkPrivacyChange(imageIds, isPublic).catch(
          (): number[] => []
        );

        if (!isMountedRef.current || saveGenerationRef.current !== generation) return;

        isSavingRef.current = false;
        setIsSaving(false);

        const succeeded = new Set(succeededIds);
        const failedIds = imageIds.filter((imageId) => !succeeded.has(imageId));

        if (failedIds.length === 0) {
          setIsManaging(false);
          setSelectedIds(new Set());
          return;
        }

        setSelectedIds(new Set(failedIds));
        setFailure({ failedCount: failedIds.length, totalCount: imageIds.length });
      };

      save().catch(() => {
        if (saveGenerationRef.current !== generation) return;

        isSavingRef.current = false;
        setIsSaving(false);
      });
    },
    [onBulkPrivacyChange, selectedIds]
  );

  return {
    isManaging,
    selectedIds,
    isSaving,
    failure,
    enterManageMode,
    cancel,
    toggleSelected,
    setSelectedPrivacy,
  };
};

export default useGalleryManageMode;
